import { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PPE_OPTIONS } from "@/lib/constants";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import RescuePlanTemplateMenu from "@/components/RescuePlanTemplateMenu";

export default function TailgateForm({ project, initial, onSaved, mode = "edit" }) {
  // Generate a stable id once per mount so create vs. edit can both go through
  // the same insert-or-update server endpoint.
  const [recordId] = useState(() => initial?.id || (window.crypto?.randomUUID?.() || `tg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`));
  const [form, setForm] = useState({
    date: initial?.date || new Date().toISOString().slice(0, 10),
    site: initial?.site || project.site_name,
    work_scope: initial?.work_scope || "",
    weather: initial?.weather || "",
    ppe_requirements: initial?.ppe_requirements || [],
    hazards: initial?.hazards || [],
    emergency_contacts: initial?.emergency_contacts || "",
    rescue_plan: initial?.rescue_plan || "",
    signatures: initial?.signatures || [],
  });
  const [hazardInput, setHazardInput] = useState("");
  const [sigName, setSigName] = useState("");
  const [busy, setBusy] = useState(false);

  const togglePPE = (item) => {
    setForm((f) => ({
      ...f,
      ppe_requirements: f.ppe_requirements.includes(item)
        ? f.ppe_requirements.filter((x) => x !== item)
        : [...f.ppe_requirements, item],
    }));
  };

  const addHazard = () => {
    if (!hazardInput.trim()) return;
    setForm({ ...form, hazards: [...form.hazards, hazardInput.trim()] });
    setHazardInput("");
  };

  const addSignature = (dataUrl) => {
    if (!sigName.trim()) { toast.error("Enter a name first"); return false; }
    setForm({
      ...form,
      signatures: [...form.signatures, { name: sigName.trim(), signature_data: dataUrl, signed_at: new Date().toISOString() }],
    });
    setSigName("");
    toast.success("Signature added");
  };

  const removeSignature = (i) => {
    setForm({ ...form, signatures: form.signatures.filter((_, idx) => idx !== i) });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        ...form,
        id: recordId,
        project_id: project.id,
        created_by: "",
      };
      const r = await api.post(`/projects/${project.id}/tailgate`, body, {
        __offlineQueueable: {
          kind: "tailgate",
          projectId: project.id,
          body,
        },
      });
      if (r.queued) {
        toast.info("Saved offline — will sync when back online");
      } else {
        toast.success(mode === "new" ? "Daily tailgate created" : "Tailgate updated");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-5 sm:p-6 space-y-4" data-testid="tailgate-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-300">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-700" data-testid="tg-date" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-300">Site</Label>
            <Input value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-700" data-testid="tg-site" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-300">Weather</Label>
            <Input value={form.weather} onChange={(e) => setForm({ ...form, weather: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-700" placeholder="Clear, 72°F, winds 5mph" data-testid="tg-weather" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-slate-300">Scope of Work — This Day</Label>
            <Textarea rows={3} value={form.work_scope} onChange={(e) => setForm({ ...form, work_scope: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-700" placeholder="Describe what the crew will be performing TODAY (separate from the project-wide JSA scope)..." data-testid="tg-work-scope" />
            <div className="text-[11px] text-slate-500 mt-1">Daily scope only — the full project scope lives on the JSA.</div>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-300 block mb-2">PPE Requirements</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PPE_OPTIONS.map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm bg-slate-950 border border-slate-700 rounded-md px-3 py-2 cursor-pointer" data-testid={`tg-ppe-${p.replace(/\s+/g, '-').toLowerCase()}`}>
                <Checkbox checked={form.ppe_requirements.includes(p)} onCheckedChange={() => togglePPE(p)} />
                <span>{p}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-300">Hazards</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={hazardInput} onChange={(e) => setHazardInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHazard(); } }} placeholder="Add a hazard..." className="bg-slate-950 border-slate-700" data-testid="tg-hazard-input" />
            <Button type="button" onClick={addHazard} className="bg-blue-700 hover:bg-blue-600" data-testid="tg-hazard-add"><Plus className="w-4 h-4"/></Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {form.hazards.map((h, i) => (
              <span key={`${i}-${h}`} className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-red-950/40 border border-red-900 text-red-300 text-sm" data-testid={`tg-hazard-${i}`}>
                {h}<button onClick={() => setForm({ ...form, hazards: form.hazards.filter((_, idx) => idx !== i) })}><X className="w-3 h-3"/></button>
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-300">Emergency Contacts</Label>
            <Textarea rows={3} value={form.emergency_contacts} onChange={(e) => setForm({ ...form, emergency_contacts: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-700" placeholder="Nearest hospital, 911 access, site contact..." data-testid="tg-emergency" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs uppercase tracking-wide text-slate-300">Rescue Plan</Label>
              <RescuePlanTemplateMenu
                currentValue={form.rescue_plan}
                onPick={(text) => setForm({ ...form, rescue_plan: text })}
                testid="tg-rescue-tpl"
              />
            </div>
            <Textarea rows={3} value={form.rescue_plan} onChange={(e) => setForm({ ...form, rescue_plan: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-700" data-testid="tg-rescue" />
          </div>
        </div>

        <div className="pt-2 border-t border-slate-800">
          <Label className="text-xs uppercase tracking-wide text-slate-300 block mb-2">Crew Signatures ({form.signatures.length})</Label>
          <div className="flex flex-wrap gap-2 mb-3">
            {form.signatures.map((s, i) => (
              <div key={`${i}-${s.signer_name || ""}`} className="bg-slate-950 border border-slate-700 rounded-md p-2 flex items-center gap-2" data-testid={`tg-sig-${i}`}>
                <img src={s.signature_data} alt={`Signature of ${s.name}`} className="h-12 bg-slate-800 border border-slate-700 rounded" />
                <div className="text-xs">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-slate-400">{new Date(s.signed_at).toLocaleString()}</div>
                </div>
                <button onClick={() => removeSignature(i)} className="text-slate-400 hover:text-red-400"><X className="w-4 h-4"/></button>
              </div>
            ))}
          </div>
          <div className="bg-slate-950 border border-slate-800 rounded-md p-3">
            <div className="flex gap-2 mb-2">
              <Input placeholder="Signer name" value={sigName} onChange={(e) => setSigName(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="tg-sig-name" />
            </div>
            <SignaturePad onSave={addSignature} testid="tg-sig-pad" />
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={submit} disabled={busy} className="bg-red-700 hover:bg-red-600 font-display tracking-wider" data-testid="tg-save">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            SAVE TAILGATE MEETING
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
